import { useCallback, useEffect, useState } from "react";

import { BucketColumn } from "@/components/dashboard/BucketColumn";
import { DashboardTopBar } from "@/components/dashboard/DashboardTopBar";
import { EmailDetailDrawer } from "@/components/dashboard/EmailDetailDrawer";
import { FyiSection } from "@/components/dashboard/FyiSection";
import { MobileBucketTabs } from "@/components/dashboard/MobileBucketTabs";
import {
  type Bucket,
  type BucketedEmails,
  emptyBuckets,
  getEmails,
} from "@/lib/emails";

const REFRESH_INTERVAL_MS = 60_000;

// FYI lives in its own collapsible section per the locked product decision,
// so the column/tab UI only ever lists the 3 actionable buckets.
const ACTIONABLE_BUCKETS: Bucket[] = [
  "need_reply_now",
  "awaiting_your_action",
  "waiting_on_client",
];

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; buckets: BucketedEmails }
  | { kind: "error"; message: string };

export function Dashboard() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [activeMobile, setActiveMobile] = useState<Bucket>("need_reply_now");
  const [signingOut, setSigningOut] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchEmails = useCallback(async () => {
    try {
      const data = await getEmails();
      setState({ kind: "ready", buckets: data.buckets });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ kind: "error", message });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchEmails();
    const interval = window.setInterval(() => {
      if (!cancelled) void fetchEmails();
    }, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [fetchEmails]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      // If logout fails we still reload — the session cookie may already be
      // expired client-side and the user just wants out.
    }
    window.location.href = "/login";
  }, []);

  const buckets =
    state.kind === "ready" ? state.buckets : emptyBuckets();

  return (
    <main className="flex min-h-screen flex-col bg-op-base text-op-primary">
      <DashboardTopBar onSignOut={handleSignOut} signingOut={signingOut} />

      <div className="flex-1 px-4 py-4 md:px-6 md:py-6">
        {state.kind === "loading" ? (
          <p
            className="rounded-lg border border-dashed border-op-border px-4 py-10 text-center text-sm text-op-muted"
            data-testid="dashboard-loading"
          >
            Loading your inbox…
          </p>
        ) : null}

        {state.kind === "error" ? (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-op-border bg-op-elevated px-4 py-3 text-sm text-op-secondary"
            data-testid="dashboard-error"
          >
            Couldn't load emails ({state.message}). We'll keep trying in the
            background.
          </div>
        ) : null}

        {state.kind !== "loading" ? (
          <>
            {/* Mobile: tabs + single-column view */}
            <div className="lg:hidden">
              <MobileBucketTabs
                buckets={buckets}
                active={activeMobile}
                onChange={setActiveMobile}
                visibleBuckets={ACTIONABLE_BUCKETS}
              />
              <div className="mt-4">
                <BucketColumn
                  bucket={activeMobile}
                  emails={buckets[activeMobile]}
                  onSelect={setSelectedId}
                />
              </div>
            </div>

            {/* Desktop: 3-up grid of actionable buckets */}
            <div className="hidden gap-6 lg:grid lg:grid-cols-3">
              {ACTIONABLE_BUCKETS.map((b) => (
                <BucketColumn
                  key={b}
                  bucket={b}
                  emails={buckets[b]}
                  onSelect={setSelectedId}
                />
              ))}
            </div>

            <FyiSection
              emails={buckets.auto_handled_fyi}
              onSelect={setSelectedId}
            />
          </>
        ) : null}
      </div>

      {selectedId ? (
        <EmailDetailDrawer
          emailId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={fetchEmails}
        />
      ) : null}
    </main>
  );
}
